import { useRef, useEffect, useCallback, memo } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Slider } from '@/components/ui/slider'
import {
  Film,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Magnet,
  Scissors,
  Gauge,
  ArrowRightLeft,
  BetweenHorizontalEnd,
  ChevronDown,
  X,
  MousePointer2,
  Undo2,
  Redo2,
  Flag,
  FlagOff,
  Activity,
  Link2,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { formatHotkeyBinding } from '@/config/hotkeys'
import { useTimelineZoom } from '../hooks/use-timeline-zoom'
import { useTimelineStore } from '../stores/timeline-store'
import { useTimelineCommandStore } from '../stores/timeline-command-store'
import { usePlaybackStore } from '@/shared/state/playback'
import { useEditorStore } from '@/app/state/editor'
import { useSelectionStore } from '@/shared/state/selection'
import {
  ZOOM_FRICTION,
  ZOOM_MIN_VELOCITY,
  ZOOM_MIN,
  ZOOM_MAX,
  SLIP_SLIDE_TOOLS_ENABLED,
} from '../constants'
import { EDITOR_LAYOUT_CSS_VALUES } from '@/app/editor-layout'
import { useResolvedHotkeys } from '@/features/timeline/deps/settings'

interface TimelineHeaderProps {
  onZoomChange?: (newZoom: number) => void
  onZoomIn?: () => void
  onZoomOut?: () => void
  onZoomToFit?: () => void
  /** Whether the color scopes tab is active in the bottom editor panel */
  isScopesPanelOpen?: boolean
  /** Callback to toggle/open the color scopes tab */
  onToggleScopesPanel?: () => void
}

function TrimEditIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <rect x="10.6" y="4" width="2.8" height="16" rx="0.75" fill="currentColor" opacity="0.72" />
      <path d="m9 7-6 5 6 5z" fill="currentColor" stroke="none" />
      <path d="m15 7 6 5-6 5z" fill="currentColor" stroke="none" />
    </svg>
  )
}

/**
 * Timeline Toolbar Component
 *
 * Unified toolbar for timeline controls:
 * - Select/Razor tools
 * - Undo/Redo
 * - In/Out points, Snap toggle
 * - Zoom controls
 */
export const TimelineHeader = memo(function TimelineHeader({
  onZoomChange,
  onZoomIn,
  onZoomOut,
  onZoomToFit,
  isScopesPanelOpen,
  onToggleScopesPanel,
}: TimelineHeaderProps) {
  const hotkeys = useResolvedHotkeys()
  const { zoomLevel, zoomIn, zoomOut, setZoom } = useTimelineZoom()
  const snapEnabled = useTimelineStore((s) => s.snapEnabled)
  const toggleSnap = useTimelineStore((s) => s.toggleSnap)
  const inPoint = useTimelineStore((s) => s.inPoint)
  const outPoint = useTimelineStore((s) => s.outPoint)
  const setInPoint = useTimelineStore((s) => s.setInPoint)
  const setOutPoint = useTimelineStore((s) => s.setOutPoint)
  const clearInOutPoints = useTimelineStore((s) => s.clearInOutPoints)
  const addMarker = useTimelineStore((s) => s.addMarker)
  // Only subscribe to marker count for disabled state - avoids re-render on marker changes
  const hasMarkers = useTimelineStore((s) => s.markers.length > 0)
  const removeMarker = useTimelineStore((s) => s.removeMarker)
  const clearAllMarkers = useTimelineStore((s) => s.clearAllMarkers)
  // NOTE: Don't subscribe to currentFrame - only needed in click handlers
  // Read from store directly when needed to avoid re-renders every frame
  const activeTool = useSelectionStore((s) => s.activeTool)
  const setActiveTool = useSelectionStore((s) => s.setActiveTool)
  const selectedMarkerId = useSelectionStore((s) => s.selectedMarkerId)
  const clearSelection = useSelectionStore((s) => s.clearSelection)
  const linkedSelectionEnabled = useEditorStore((s) => s.linkedSelectionEnabled)
  const setLinkedSelectionEnabled = useEditorStore((s) => s.setLinkedSelectionEnabled)
  const canUndo = useTimelineCommandStore((s) => s.canUndo)
  const canRedo = useTimelineCommandStore((s) => s.canRedo)
  const undoLabel = useTimelineCommandStore((s) => s.getUndoLabel())
  const redoLabel = useTimelineCommandStore((s) => s.getRedoLabel())

  const SlipSlideFlyoutIcon = activeTool === 'slide' ? BetweenHorizontalEnd : ArrowRightLeft

  const btnSize = {
    width: EDITOR_LAYOUT_CSS_VALUES.toolbarButtonSize,
    height: EDITOR_LAYOUT_CSS_VALUES.toolbarButtonSize,
  } as const

  // Momentum state for zoom slider
  const zoomVelocityRef = useRef(0)
  const lastZoomValueRef = useRef(zoomLevel)
  const lastZoomTimeRef = useRef(0)
  const momentumIdRef = useRef<number | null>(null)
  const sliderRafIdRef = useRef<number | null>(null)
  const queuedSliderZoomRef = useRef<number | null>(null)
  const isDraggingRef = useRef(false)
  const zoomLevelRef = useRef(zoomLevel)
  zoomLevelRef.current = zoomLevel

  // Apply zoom with bounds checking
  const applyZoom = useCallback(
    (newZoom: number) => {
      const clampedZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoom))
      if (onZoomChange) {
        onZoomChange(clampedZoom)
      } else {
        setZoom(clampedZoom)
      }
      return clampedZoom
    },
    [onZoomChange, setZoom],
  )

  // Momentum loop for zoom slider
  const startZoomMomentum = useCallback(() => {
    if (momentumIdRef.current !== null) {
      cancelAnimationFrame(momentumIdRef.current)
    }

    const momentumLoop = () => {
      if (Math.abs(zoomVelocityRef.current) > ZOOM_MIN_VELOCITY) {
        const newZoom = zoomLevelRef.current + zoomVelocityRef.current
        const clampedZoom = applyZoom(newZoom)

        // Stop momentum if we hit bounds
        if (clampedZoom <= ZOOM_MIN || clampedZoom >= ZOOM_MAX) {
          zoomVelocityRef.current = 0
          momentumIdRef.current = null
          return
        }

        zoomVelocityRef.current *= ZOOM_FRICTION
        momentumIdRef.current = requestAnimationFrame(momentumLoop)
      } else {
        zoomVelocityRef.current = 0
        momentumIdRef.current = null
      }
    }

    momentumIdRef.current = requestAnimationFrame(momentumLoop)
  }, [applyZoom])

  // Convert between linear slider position (0-1) and logarithmic zoom level
  // This gives finer control at low zoom levels
  const sliderToZoom = useCallback((sliderValue: number) => {
    // Map 0-1 to log scale: ZOOM_MIN to ZOOM_MAX
    // Using exponential: zoom = min * (max/min)^slider
    return ZOOM_MIN * Math.pow(ZOOM_MAX / ZOOM_MIN, sliderValue)
  }, [])

  const zoomToSlider = useCallback((zoom: number) => {
    // Inverse of sliderToZoom: slider = log(zoom/min) / log(max/min)
    return Math.log(zoom / ZOOM_MIN) / Math.log(ZOOM_MAX / ZOOM_MIN)
  }, [])

  // Handle slider value change (while dragging)
  const handleSliderChange = useCallback(
    (values: number[]) => {
      const sliderValue = values[0] ?? 0.5
      const newZoom = sliderToZoom(sliderValue)
      const now = performance.now()
      const timeDelta = now - lastZoomTimeRef.current

      // Calculate velocity based on change over time (in zoom space, not slider space)
      if (timeDelta > 0 && timeDelta < 100) {
        const valueDelta = newZoom - lastZoomValueRef.current
        zoomVelocityRef.current = (valueDelta / timeDelta) * 16 // Normalize to ~60fps
      }

      lastZoomValueRef.current = newZoom
      lastZoomTimeRef.current = now
      isDraggingRef.current = true
      queuedSliderZoomRef.current = newZoom
      if (sliderRafIdRef.current === null) {
        sliderRafIdRef.current = requestAnimationFrame(() => {
          sliderRafIdRef.current = null
          const queuedZoom = queuedSliderZoomRef.current
          if (queuedZoom !== null) {
            applyZoom(queuedZoom)
          }
        })
      }
    },
    [applyZoom, sliderToZoom],
  )

  // Handle slider release - start momentum
  const handleSliderCommit = useCallback(() => {
    isDraggingRef.current = false
    if (sliderRafIdRef.current !== null) {
      cancelAnimationFrame(sliderRafIdRef.current)
      sliderRafIdRef.current = null
    }
    if (queuedSliderZoomRef.current !== null) {
      applyZoom(queuedSliderZoomRef.current)
      queuedSliderZoomRef.current = null
    }
    // Only start momentum if there's meaningful velocity
    if (Math.abs(zoomVelocityRef.current) > ZOOM_MIN_VELOCITY) {
      startZoomMomentum()
    }
    // Blur slider to release focus for keyboard shortcuts (play/pause)
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [applyZoom, startZoomMomentum])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (momentumIdRef.current !== null) {
        cancelAnimationFrame(momentumIdRef.current)
      }
      if (sliderRafIdRef.current !== null) {
        cancelAnimationFrame(sliderRafIdRef.current)
      }
    }
  }, [])

  const handleUndo = () => {
    useTimelineStore.temporal.getState().undo()
  }

  const handleRedo = () => {
    useTimelineStore.temporal.getState().redo()
  }

  return (
    <div
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-border px-3"
      style={{ height: EDITOR_LAYOUT_CSS_VALUES.timelineHeaderHeight }}
      role="toolbar"
      aria-label="时间线控制"
    >
      {/* Left: Title */}
      <div className="flex min-w-0 items-center gap-2.5">
        <h2 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
          <Film className="w-3 h-3" />
          时间线
        </h2>
      </div>

      {/* Middle: Timeline Controls */}
      <div className="min-w-0 overflow-x-auto overflow-y-hidden">
        <div className="flex w-max min-w-full items-center justify-center gap-2.5">
          {/* Timeline Tools */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              className={
                activeTool === 'select'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : ''
              }
              onClick={() => setActiveTool('select')}
              aria-label="选择工具"
              data-tooltip="选择工具 (V)"
            >
              <MousePointer2 className="w-3.5 h-3.5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              className={
                activeTool === 'trim-edit'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : ''
              }
              onClick={() => setActiveTool(activeTool === 'trim-edit' ? 'select' : 'trim-edit')}
              aria-label="修剪编辑工具"
              data-tooltip="修剪编辑工具 (T)"
            >
              <TrimEditIcon className="w-3.5 h-3.5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              className={
                activeTool === 'razor'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : ''
              }
              onClick={() => setActiveTool(activeTool === 'razor' ? 'select' : 'razor')}
              aria-label="剃刀工具"
              data-tooltip="剃刀工具 (C)"
            >
              <Scissors className="w-3.5 h-3.5 -rotate-90" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              className={
                activeTool === 'rate-stretch'
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : ''
              }
              onClick={() =>
                setActiveTool(activeTool === 'rate-stretch' ? 'select' : 'rate-stretch')
              }
              aria-label="速率伸缩工具"
              data-tooltip="速率伸缩工具 (R)"
            >
              <Gauge className="w-3.5 h-3.5" />
            </Button>

            {SLIP_SLIDE_TOOLS_ENABLED ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    style={{ height: EDITOR_LAYOUT_CSS_VALUES.toolbarButtonSize }}
                    className={`gap-1 px-2 ${
                      activeTool === 'slip' || activeTool === 'slide'
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                        : ''
                    }`}
                    aria-label="滑移和滑动工具"
                    data-tooltip="滑移 / 滑动工具"
                  >
                    <span className="flex items-center gap-1">
                      <span className="inline-flex items-center justify-center">
                        <SlipSlideFlyoutIcon className="w-3.5 h-3.5" />
                      </span>
                      <ChevronDown className="w-3 h-3 opacity-70" />
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => setActiveTool(activeTool === 'slip' ? 'select' : 'slip')}
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5" />
                    <span className="flex-1">滑移工具</span>
                    <span className="text-xs text-muted-foreground">
                      {formatHotkeyBinding(hotkeys.SLIP_TOOL)}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setActiveTool(activeTool === 'slide' ? 'select' : 'slide')}
                  >
                    <BetweenHorizontalEnd className="w-3.5 h-3.5" />
                    <span className="flex-1">滑动工具</span>
                    <span className="text-xs text-muted-foreground">
                      {formatHotkeyBinding(hotkeys.SLIDE_TOOL)}
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          <Separator orientation="vertical" className="h-5 mx-1.5" />

          {/* Undo/Redo */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={handleUndo}
              disabled={!canUndo}
              aria-label={undoLabel ? `撤销 ${undoLabel}` : '撤销'}
              data-tooltip={undoLabel ? `撤销 ${undoLabel} (Ctrl+Z)` : '撤销 (Ctrl+Z)'}
            >
              <Undo2 className="w-3.5 h-3.5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={handleRedo}
              disabled={!canRedo}
              aria-label={redoLabel ? `重做 ${redoLabel}` : '重做'}
              data-tooltip={redoLabel ? `重做 ${redoLabel} (Ctrl+Shift+Z)` : '重做 (Ctrl+Shift+Z)'}
            >
              <Redo2 className="w-3.5 h-3.5" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-5 mx-1.5" />

          {/* In/Out Points */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={() => setInPoint(usePlaybackStore.getState().currentFrame)}
              aria-label="设置入点"
              data-tooltip="设置入点 (I)"
            >
              <span className="text-sm font-bold" style={{ color: 'var(--color-timeline-in)' }}>
                [
              </span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={() => setOutPoint(usePlaybackStore.getState().currentFrame)}
              aria-label="设置出点"
              data-tooltip="设置出点 (O)"
            >
              <span className="text-sm font-bold" style={{ color: 'var(--color-timeline-out)' }}>
                ]
              </span>
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={clearInOutPoints}
              disabled={inPoint === null && outPoint === null}
              aria-label="清除入点和出点"
              data-tooltip="清除入点/出点"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-5 mx-1.5" />

          {/* Markers */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={() => addMarker(usePlaybackStore.getState().currentFrame)}
              aria-label="添加标记"
              data-tooltip="添加标记 (M)"
            >
              <Flag className="w-3.5 h-3.5" style={{ color: 'var(--color-timeline-marker)' }} />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={() => {
                if (selectedMarkerId) {
                  removeMarker(selectedMarkerId)
                  clearSelection()
                }
              }}
              disabled={!selectedMarkerId}
              aria-label="移除所选标记"
              data-tooltip="移除所选标记"
            >
              <FlagOff className="w-3.5 h-3.5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              style={btnSize}
              onClick={clearAllMarkers}
              disabled={!hasMarkers}
              aria-label="清除全部标记"
              data-tooltip="清除全部标记"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          <Separator orientation="vertical" className="h-5 mx-1.5" />

          {/* Snap Toggle */}
          <Button
            variant="ghost"
            size="icon"
            style={btnSize}
            className={snapEnabled ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}
            onClick={toggleSnap}
            aria-label={snapEnabled ? '关闭吸附' : '启用吸附'}
            data-tooltip={snapEnabled ? '吸附已开启' : '吸附已关闭'}
          >
            <Magnet className="w-3.5 h-3.5" />
          </Button>

          <Separator orientation="vertical" className="h-5 mx-1.5" />

          {/* Editor Panel Toggles */}
          <Button
            variant="ghost"
            size="icon"
            style={btnSize}
            className={
              isScopesPanelOpen ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''
            }
            onClick={onToggleScopesPanel}
            aria-label={isScopesPanelOpen ? '隐藏颜色示波器' : '显示颜色示波器'}
            data-tooltip={isScopesPanelOpen ? '隐藏颜色示波器' : '显示颜色示波器'}
          >
            <Activity className="w-3.5 h-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            style={btnSize}
            className={
              linkedSelectionEnabled ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''
            }
            onClick={() => setLinkedSelectionEnabled(!linkedSelectionEnabled)}
            aria-label={
              linkedSelectionEnabled ? '关闭联动选择' : '启用联动选择'
            }
            aria-pressed={linkedSelectionEnabled}
            data-tooltip={`${linkedSelectionEnabled ? '联动选择已开启' : '联动选择已关闭'} (${formatHotkeyBinding(hotkeys.TOGGLE_LINKED_SELECTION)})`}
          >
            <Link2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Right: Zoom Controls */}
      <div className="flex items-center justify-end gap-1.5">
        <Button
          variant="ghost"
          size="icon"
          style={btnSize}
          onClick={() => {
            if (onZoomOut) {
              onZoomOut()
            } else {
              zoomOut()
            }
          }}
          aria-label="缩小"
          data-tooltip="缩小"
        >
          <ZoomOut className="w-3.5 h-3.5" />
        </Button>

        <Slider
          value={[zoomToSlider(zoomLevel)]}
          onValueChange={handleSliderChange}
          onValueCommit={handleSliderCommit}
          min={0}
          max={1}
          step={0.005}
          className="w-24"
          aria-label="时间线缩放"
        />

        <Button
          variant="ghost"
          size="icon"
          style={btnSize}
          onClick={() => {
            if (onZoomIn) {
              onZoomIn()
            } else {
              zoomIn()
            }
          }}
          aria-label="放大"
          data-tooltip="放大"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          style={btnSize}
          onClick={onZoomToFit}
          aria-label="缩放以适应"
          data-tooltip="缩放以适应 (Z)"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  )
})
